import { Component, inject, ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { MenuItem } from '@openng/optimus-ui/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { DatabaseUser } from 'src/modules/database/shared/database-user.entity';
import { ChangeSummary } from 'src/modules/enaio/netgen/ChangeSummary';
import { SmTableComponent } from 'src/modules/sm-base/components/sm-table/sm-table.component';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableCellEvent } from 'src/modules/sm-base/models/table-cell-event.model';
import { TableCell } from 'src/modules/sm-base/models/table-cell.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { ToolTipDefinition } from 'src/modules/sm-base/models/ToolTipDefinition.model';
import { FrontendFieldAddon } from 'src/modules/sm-base/models/frontend-field-addon.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { ArrayOrSingle } from 'src/modules/sm-base/shared/array-or-single.model';
import { GuiUtils } from "src/modules/utils/misc/gui-utils";
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Dates } from 'src/modules/utils/shared/dates';
import { KeyValuePair } from 'src/modules/utils/shared/key-value-pair.model';
import { Objects } from 'src/modules/utils/shared/objects';
import { Strings } from 'src/modules/utils/shared/strings';
import { Utils } from 'src/modules/utils/shared/utils';
import { CalendarEvent } from '../../netgen/CalendarEvent';
import { CalendarEventKind } from '../../netgen/CalendarEventKind';
import { CalendarEventStartRelativeTo } from '../../netgen/CalendarEventStartRelativeTo';
import { Project } from '../../netgen/Project';
import { ProjectStatistics } from '../../netgen/ProjectStatistics';
import { ProjectPlanningService } from '../../services/project-planning.service';
import { CalendarEventToolTipComponent } from '../calendar-event-tool-tip/calendar-event-tool-tip.component';

@Component({
    selector: 'app-planning-view-projects1',
    templateUrl: './planning-view-projects1.component.html',
    styleUrl: './planning-view-projects1.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PlanningViewProjects1Component extends ComponentView {

    service = inject(ProjectPlanningService);

    user: DatabaseUser;
    projects: Project[];
    project: Project;
    showFinishedProjects = false;
    events: CalendarEvent[];
    filteredEvents: CalendarEvent[];
    stats: ProjectStatistics;
    customFields: KeyValuePair<string, string>[] = [];

    table: TableData;
    form: FrontendForm;
    selectedEvent: ArrayOrSingle<TableRow> = null;
    highlightedEvents: CalendarEvent[] = [];

    @ViewChild("tableComponent")
    tableComponent: SmTableComponent;

    async initParams(): Promise<boolean> {
        await this.service.getEventTypes();
        await this.service.getProjectTypes();
        await this.service.getOutlineLevels();
        await this.service.getProjectCustomFieldDefinitions();
        this.user = this.service.getCurrentUser();
        this.form = new FrontendForm([
            new FrontendField("project", this.app.t("entity.Project.entityName"), FrontendFieldType.dropdown, { 
                dropdownCanFilter: true,
                dropdownFilterBy: "label,projectNumber",
                addOnButtons: [this.createShowFinishedProjectsButton()],
                onValueChanged: this.projectChanged.bind(this)
            }),
            new FrontendField("outline", this.app.t("entity.CalendarOutlineLevel.entityName"), FrontendFieldType.dropdown, { 
                dropdownAutoComplete: true,
                listItems: [],
                onValueChanged: this.updateTable.bind(this) 
            }),
            new FrontendField("splitter1", "", FrontendFieldType.splitter),
            new FrontendField("statusActive", this.app.t("ProjectPlanning.activeTasks"), FrontendFieldType.bool, { 
                value: true,
                onValueChanged: this.updateTable.bind(this)  
            }),
            new FrontendField("statusFinished", this.app.t("ProjectPlanning.finishedTasks"), FrontendFieldType.bool, {
                onValueChanged: this.updateTable.bind(this) 
            })            
        ]);

        this.initTable();
        await this.projectsChanged();

        this.addSubscription(this.app.registerChangesCallback(this.serviceChanged.bind(this), "PlanningViewProjects1"));

        return true;
    }

    async serviceChanged(summary: ChangeSummary): Promise<void> {
        if (summary.hasAnyType(["Project", "CalendarEvent", "CalendarEventRole"])) {
            await this.projectsChanged();
            await this.projectChanged();
        }           
    }

    async setProject(p: Project): Promise<void> {
        this.form.setValue("project", p.id);
        await this.projectChanged();
    }

    async showEvent(event: CalendarEvent): Promise<void> {
        let project = (await this.service.getProjects()).find(item => item.id == event.projectId);
        if (project == null) {
            await this.app.dialog.info(this.app.t("ProjectPlanning.globalSearchProjectNotFound", { eventId: event.id }), this.app.t("ProjectPlanning.globalSearch"));
            return;
        }

        if (project.finished) {
            this.showFinishedProjects = true;
            this.updateProjectListItems();
        }
        this.form.setValue(event.finished ? "statusFinished" : "statusActive", true);
        await this.setProject(project);
        this.form.setValue("outline", null);
        this.updateTable();

        let row = this.table.rows.find(item => item.id == event.id.toString());
        if (row == null) {
            await this.app.dialog.info(this.app.t("ProjectPlanning.globalSearchEventNotVisible", { eventId: event.id }), this.app.t("ProjectPlanning.globalSearch"));
            return;
        }
        GuiUtils.angularTimerWithRetry(() => {
            this.tableComponent.grid.api.getRowNode(row.id)?.setSelected(true, true);
            this.tableComponent.makeRowVisible(row.id);
        }, () => this.tableComponent?.grid?.api?.getRowNode(row.id) != null, 100, 30);
    }

    async projectsChanged(): Promise<void> {
        this.projects = await this.service.getProjects();
        let f = this.form.getField("project");
        let sel = f.getValue();
        this.updateProjectListItems();
        if (!f.listItems.some(li => li.value == sel)) {
            f.setValue(null);
        }
    }

    updateProjectListItems(): void {
        let f = this.form.getField("project");
        let projects = this.showFinishedProjects ? this.projects : this.projects.filter(item => !item.finished);
        f.listItems = FrontendFieldListItem.make(projects, item => [item.id, item.title, this.getProjectListItemOptions(item)], true, [[null, ""]]);
    }

    createShowFinishedProjectsButton(): FrontendFieldAddon {
        return new FrontendFieldAddon("showFinishedProjects", "Abgeschlossene Projekte anzeigen", "far fa-flag-slash", true, addOn => {
            this.showFinishedProjects = !this.showFinishedProjects;
            addOn.text = this.showFinishedProjects ? "Abgeschlossene Projekte ausblenden" : "Abgeschlossene Projekte anzeigen";
            addOn.icon = this.showFinishedProjects ? "fas fa-flag" : "far fa-flag-slash";
            this.updateProjectListItems();
            let selectedProjectId = this.form.getValueNumber("project");
            if (!this.showFinishedProjects && this.projects.find(project => project.id == selectedProjectId)?.finished) {
                this.form.setValue("project", null, true);
            }
        });
    }

    getProjectListItemOptions(item: Project): Partial<FrontendFieldListItem> & { projectNumber: string } {
        return {
            html: true,
            labelInDropdown: item.getHtmlTitle(true),
            comparable: (item.finished ? "1" : "0") + item.title,
            projectNumber: item.projectNumber
        };
    }

    async projectChanged(): Promise<void> {
        let p = this.projects.find(p2 => p2.id == this.form?.getValueNumber("project"));
        let dto = p != null ? await this.service.getProjectDto(p.id) : null;
        if (dto == null) {
            this.table.rows = [];
            this.project = null;
            this.events = null;
            this.stats = null;
            this.form.setValue("project", null);
            return;
        }
        this.project = dto.project;
        this.events = dto.events;
        this.stats = dto.stats;

        if (this.project?.customFields != null) {
            let fieldDefs = await this.service.getProjectCustomFieldDefinitions();
            this.project.customFields = Arrays.sortBy(this.project.customFields, cf => fieldDefs.find(fd => fd.id == cf.fieldDefinitionId)?.order ?? 0);
            this.customFields = this.project.customFields.map(cf => new KeyValuePair<string, string>(fieldDefs.find(fd => fd.id == cf.fieldDefinitionId)?.title ?? "???", cf.value));
        }

        this.form.getField("outline").listItems = FrontendFieldListItem.make(this.stats.structurePaths, item => [item, this.service.formatStructurePathString(item)], true, [[null, this.app.t("ProjectPlanning.all")]]);
        this.form.setValue("outline", this.form.getField("outline").listItems[0]);
        this.updateTable();
    }

    initTable(): void {
        let anySubTasks = this.filteredEvents?.some(e => !Utils.isNoe(e.subTasks)); 
        this.table = TableData.update(this.table, [
            this.service.getTableColumn("userName"),            
            this.service.getTableColumn("title").with({
                cellRenderer: evt => {
                    let ce = Objects.castOrNull((evt.data as TableRow).raw, CalendarEvent);
                    if (ce == null) {
                        return evt.value;
                     }
                     return "<span>" + evt.value + "</span>"
                        + (ce?.startRelativeTo == CalendarEventStartRelativeTo.task || this.events.some(ce2 => ce2.getStartRelativeToEventId() == ce.id) ? "<i class=\"ml-2 fas fa-link font-size-075rem\" title=\"\">" : "");
                },
                getStyleClassCallback: evt => evt.row.raw instanceof CalendarEvent && this.highlightedEvents.some(ce =>  ce.id == evt.row.raw.id) ? "xbg-green-200": ""
            }),
            anySubTasks ? this.service.getTableColumn("subTasks") : null,
            this.service.getTableColumn("kind"),
            this.service.getTableColumn("start"),
            this.service.getTableColumn("end"),
            this.service.getTableColumn("plannedTime"),
            this.service.getTableColumn("doneTime"),            
            ...Utils.getRange(1, this.stats?.maxStructurePathDepth ?? 1).map(index => new TableColumn("structurePath" + index, this.app.t("ProjectPlanning.outlineLevelWithIndex", { index }), TableCellType.string, 30))                        
        ], [], ["start"]);
    }

    updateTable(): void {
        if (this.events == null) {
            return;
        }
        let outline = this.form.getValue("outline");
        let statusActive = this.form.getValueBool("statusActive");
        let statusFinished = this.form.getValueBool("statusFinished");
        this.filteredEvents = this.events.filter(e => (outline == null || e.structurePath == outline) && (statusActive || e.finished) && (statusFinished || !e.finished));
        this.initTable();
        let now = new Date();
        let structurePathIndexes = Utils.getRange(1, this.stats.maxStructurePathDepth);
        this.table.rows = this.filteredEvents.map(e => {
            let finishedStyleClass = e.finished ? "xbg-gray-400" : "";
            return new TableRow(Objects.toString(e.id), e, {
            userName: new TableCell(
                        e.getAllEditorsText(this.service, true),
                        { styleClass: finishedStyleClass }
                    ),
                    title: new TableCell(e.title, {
                        styleClass: finishedStyleClass,
                        toolTip: new ToolTipDefinition(CalendarEventToolTipComponent, { event: e })}),
            kind: new TableCell(
                        CalendarEventKind.getDisplayShortName(e.kind, this.app),
                        { styleClass: finishedStyleClass }
                    ),
                    subTasks: new TableCell(
                        Utils.isNoe(e.subTasks) ? "" : e.subTasks.filter(st => st.finished).length + "/" + e.subTasks.length,
                        { styleClass: finishedStyleClass }
                    ),
                    start: new TableCell(e.startDate, {
                        styleClass: finishedStyleClass
                    }),
                    end: new TableCell(e.allDay ? Dates.add(e.endDate, "day", -1) : e.endDate, { styleClass: e.endDate < now && !e.finished ? "xbg-red-300" : finishedStyleClass
                        }),
            plannedTime: e.plannedTime,
            doneTime: new TableCell(e.doneTime, { styleClass: e.doneTime > e.plannedTime ? " xbg-red-300" : ""}),
            ...Arrays.toOrdinaryObject(structurePathIndexes, index => "structurePath" + index, index => Strings.def(Arrays.getSafe(e.getStructurePathTitles(this.service), index - 1))),                     
        }, { 
            getContextMenuItems: this.getContextMenuItems.bind(this)
        });
        });

        this.table.rows.push(new TableRow("", null, {
            structurePath: new TableCell(this.app.t("ProjectPlanning.todayUpper"), { colSpan: -1 }),
            start: now,
            end: now
        }, { virtual: true, height: 3, styleClass: "xbg-red-300"}));

        let i = 0;
        for (let deliveryDate of [this.project.deliveryDate1, this.project.deliveryDate2, this.project.deliveryDate3]) {
            i++;
            if (deliveryDate != null) {
                this.table.rows.push(new TableRow("", null, {
                    structurePath: new TableCell(this.app.t("ProjectPlanning.deliveryDate"), { colSpan: -1 }),
                    title: this.app.t("ProjectPlanning.deliveryDateWithIndex", { index: i }),
                    start: deliveryDate.toDate(),
                    end: deliveryDate.toDate()
                }, { virtual: true, /*height: 3,*/ styleClass: "xbg-green-300"}));
            }
        }

    }

    getContextMenuItems(e: TableCellEvent): MenuItem[] {
        return this.service.getEventContextMenuItems(e.row.raw as CalendarEvent, this.user, this.projectChanged.bind(this), this);
    }

    async cellEdited(event: TableCellEvent): Promise<void> {
        if (event.column.colId == "doneTime") {
            let evt = event.row.raw as CalendarEvent;
            await RestEndpoint.main().query({id: evt.id, doneTime: evt.doneTime}).post().run("api/projectplanning/calendarEvent/setDoneTime").getText();
        }
    }

    async eventClicked(row: TableRow): Promise<void> {
        let evt = row.raw as CalendarEvent;
        if (evt != null) {
            await this.service.editEvent(evt, this.projectChanged.bind(this));        
        }
    }

    rowSelected(row: TableRow): void {
        let sel = row?.raw as CalendarEvent;
        this.highlightedEvents = sel != null ? this.events.filter(e => e.getStartRelativeToEventId() == sel.id || sel.getStartRelativeToEventId() == e.id) : [];
        this.tableComponent.refresh();
    }

}
